/**
 * Ranged aim — the client-side reading of the sim's straight line-of-fire.
 *
 * This is deliberately a projection, not an authority. `combat.ts` repeats the
 * same range and map checks before it accepts a shot; keeping this pure lets the
 * overlay tell the player what it sees without ever spending an arrow locally.
 */

import { TILE_DEFS, type Direction, type TileKind } from '@annwn/protocol';

export interface AimPoint {
  readonly x: number;
  readonly y: number;
}

export interface AimMap {
  tileAt(x: number, y: number): TileKind;
}

export interface RangedAimReadout {
  readonly range: number;
  readonly clear: boolean;
  readonly wind: Direction;
  /** Every tile the line crosses, source and target inclusive. */
  readonly line: readonly AimPoint[];
}

/**
 * The same bounded Bresenham walk the sim's `hasLineOfSight` uses.  Tiles that
 * cannot be stood in block shots, while both endpoints remain targetable — a
 * foe in a doorway is still visible and still a valid target.
 */
export function lineOfFire(
  map: AimMap,
  from: AimPoint,
  to: AimPoint,
  wind: Direction,
): RangedAimReadout {
  let x = from.x;
  let y = from.y;
  const dx = Math.abs(to.x - from.x);
  const dy = Math.abs(to.y - from.y);
  const sx = from.x < to.x ? 1 : -1;
  const sy = from.y < to.y ? 1 : -1;
  let error = dx - dy;
  const line: AimPoint[] = [];
  let clear = true;

  for (let step = 0; step < dx + dy + 2; step += 1) {
    line.push({ x, y });
    if (x === to.x && y === to.y) break;
    if (!(x === from.x && y === from.y) && !TILE_DEFS[map.tileAt(x, y)].passable) {
      clear = false;
      break;
    }

    const doubled = error * 2;
    if (doubled > -dy) {
      error -= dy;
      x += sx;
    } else if (doubled < dx) {
      error += dx;
      y += sy;
    }
  }

  return { range: Math.max(dx, dy), clear, wind, line };
}

export function aimReadoutText(readout: RangedAimReadout): string {
  return `${readout.range} tiles · line ${readout.clear ? 'clear' : 'blocked'} · wind ${readout.wind}`;
}
